import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as BillingPageIcon } from '../assets/icons/billing-page.svg';
import { ReactComponent as DownloadCircleIcon } from '../assets/icons/download-circle.svg';
import { ReactComponent as ChevronRightThinIcon } from '../assets/icons/chevron-right-thin.svg';
import { ReactComponent as ChevronDownThinIcon } from '../assets/icons/chevron-down-thin.svg';
import PageHeader from '../components/PageHeader';

import { useAuth } from '../auth/AuthContext';
import api from '../services/api';
import { BillingRecord } from '../types/index';

const ITEMS_PER_PAGE = 10;

const BillingPage: React.FC = () => {
  const { storeId } = useAuth();

  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecords = async () => {
      if (!storeId) return;
      try {
        setLoading(true);
        const data = await api.billing.getRecords(storeId);
        setRecords(data.records || []);
        setError(null);
      } catch {
        setError('利用明細の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, [storeId]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDownloadCsv = async (record: BillingRecord) => {
    if (!storeId || downloadingId) return;
    try {
      setDownloadingId(record.id);
      const blob = await api.billing.downloadCsv(storeId, record.year, record.month);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `利用明細_${record.year}年${record.month}月.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      setError('CSVのダウンロードに失敗しました');
    } finally {
      setDownloadingId(null);
    }
  };


  const totalPages = Math.ceil(records.length / ITEMS_PER_PAGE);
  const pagedRecords = records.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // 請求額の目安：未確定行の総請求額（なければ最新行）
  const estimatedAmount = records.find(r => !r.is_confirmed)?.total_amount ?? records[0]?.total_amount ?? 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PageHeader
        title="利用明細"
        icon={<SvgIcon component={BillingPageIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      <Container maxWidth="md" sx={{ pb: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* セクション1: 請求額の目安 */}
          <Card variant="outlined" sx={{ borderRadius: '10px' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    請求額の目安
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 , color: '#3c3c3c'}}>
                    {estimatedAmount.toLocaleString()}円
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* セクション2: 請求期間一覧 */}
          <Card variant="outlined" id="billing-list" sx={{ borderRadius: '10px' }}>
            <CardContent sx={{ p: 0 }}>
              {/* ページネーション（上部） */}
              {records.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Button
                    size="small"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    sx={{ minWidth: 'auto' }}
                  >
                    前へ
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <Button
                      key={p}
                      size="small"
                      variant={p === page ? 'contained' : 'text'}
                      onClick={() => setPage(p)}
                      sx={{ minWidth: 28, width: 28, height: 28, borderRadius: '50%', padding: 0 }}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    size="small"
                    disabled={page === totalPages || totalPages === 0}
                    onClick={() => setPage(p => p + 1)}
                    sx={{ minWidth: 'auto' }}
                  >
                    次へ
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {(page - 1) * ITEMS_PER_PAGE + 1}〜
                    {Math.min(page * ITEMS_PER_PAGE, records.length)}件表示中／{records.length}件中
                  </Typography>
                </Box>
              )}

              {/* 明細行一覧 */}
              {records.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">利用明細はありません</Alert>
                </Box>
              ) : (
                pagedRecords.map((record, index) => {
                  const isExpanded = expandedIds.has(record.id);
                  return (
                    <React.Fragment key={record.id}>
                      {index > 0 && <Divider />}

                      {/* 行ヘッダー */}
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography
                            variant="body1"
                            color="primary"
                            sx={{ flex: 1 }}
                          >
                            {record.period}
                          </Typography>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body1" sx={{ mr: 1 , color: '#3c3c3c'}}>
                              {record.total_amount.toLocaleString()}円
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={downloadingId === record.id}
                              onClick={() => handleDownloadCsv(record)}
                              sx={{
                                p: 0,
                                color: '#0071e3',
                                '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                              }}
                            >
                              {downloadingId === record.id
                                ? <CircularProgress size={24} />
                                : <SvgIcon component={DownloadCircleIcon} inheritViewBox sx={{ fontSize: 24, color: '#0071e3' }} />}
                            </IconButton>
                            <IconButton size="small" onClick={() => toggleExpand(record.id)}>
                              {isExpanded
                                ? <SvgIcon component={ChevronDownThinIcon} inheritViewBox sx={{ fontSize: 14, color: '#0071e3' }} />
                                : <SvgIcon component={ChevronRightThinIcon} inheritViewBox sx={{ fontSize: 14, color: '#0071e3' }} />
                              }
                            </IconButton>
                          </Stack>
                        </Stack>

                        {/* 展開コンテンツ */}
                        {isExpanded && (
                          <Box sx={{ mt: 1.5, pl: 1 , mr: 2}}>
                            <Stack spacing={1}>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>システム利用料（税別）</Typography>
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>{record.system_usage_fee.toLocaleString()}円</Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>対象人数</Typography>
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>{record.target_count}人</Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>立替払い金額（税別）</Typography>
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>
                                  {(record.prepaid_amount_total ?? record.prepaid_offer_amount_total ?? 0).toLocaleString()}円
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>振込関連手数料（税別）</Typography>
                                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>{(record.transfer_fee_total ?? record.transfer_fee).toLocaleString()}円</Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </React.Fragment>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default BillingPage;
